import React, { useState, useEffect } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const InstallAppButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Check if running on iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                        window.navigator.standalone === true;
    
    if (isStandalone) {
      setShowInstallButton(false);
      return;
    }

    // Listen for PWA install prompt (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show button for iOS users (no beforeinstallprompt event) and all mobile/desktop users
    if (iOS) {
      setShowInstallButton(true);
    } else {
      // Show for all users (they can always download/install)
      setShowInstallButton(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS installation instructions
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      // Android/Desktop PWA install - trigger directly
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setShowInstallButton(false);
        console.log('App installed successfully!');
      } else {
        console.log('User dismissed install prompt');
      }
      
      setDeferredPrompt(null);
    } else {
      // Fallback: show instructions for browsers that don't support beforeinstallprompt
      alert('To install the app:\n\n• Android: Tap the menu (⋮) and select "Install app" or "Add to Home screen"\n• iOS: Tap the Share button (⎙) and select "Add to Home Screen"');
    }
  };

  if (!showInstallButton) {
    return null;
  }

  return (
    <>
      {/* Install App Button */}
      <button
        onClick={handleInstallClick}
        className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white px-6 py-3 rounded-xl shadow-lg transition-all duration-300 hover:scale-105 flex items-center gap-2 font-semibold"
        title="Install App"
      >
        <Smartphone className="w-5 h-5" />
        <span>Install App</span>
      </button>

      {/* iOS Installation Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-cyan-500/30 max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Download className="w-6 h-6 text-cyan-400" />
                Install EdgeTrade App
              </h3>
              <button
                onClick={() => setShowIOSInstructions(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 text-gray-300">
              <p className="text-sm">
                To install EdgeTrade on your iPhone or iPad:
              </p>

              <ol className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xs font-bold">
                    1
                  </span>
                  <span>
                    Tap the <strong className="text-white">Share button</strong> 
                    <span className="inline-block mx-1 px-2 py-1 bg-blue-500/20 rounded text-blue-300">⎙</span> 
                    at the bottom of Safari
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xs font-bold">
                    2
                  </span>
                  <span>
                    Scroll down and tap <strong className="text-white">"Add to Home Screen"</strong>
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xs font-bold">
                    3
                  </span>
                  <span>
                    Tap <strong className="text-white">"Add"</strong> in the top right corner
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xs font-bold">
                    4
                  </span>
                  <span>
                    The EdgeTrade app will now appear on your home screen!
                  </span>
                </li>
              </ol>

              <div className="mt-6 p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-xs text-cyan-300">
                  💡 <strong>Tip:</strong> Once installed, the app works offline and loads faster!
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowIOSInstructions(false)}
              className="w-full mt-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white font-semibold rounded-lg transition-all"
            >
              Got it!
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
